import { useMemo } from 'react'
import {
	TrendingUp,
	TrendingDown,
	Clock,
	Calendar,
	Lightbulb,
} from 'lucide-react'
import { Badge } from '@/common/ui/badge'

import type { GetActiveUsersSummaryResponse } from '@/modules/dashboard/type'
import {
	formatUserCount,
	formatHour,
	findPeak,
	findLowest,
	type PeakLowData,
} from '../../utils'

type ComparisonInsightsProps = {
	data?: GetActiveUsersSummaryResponse
	comparisonData?: GetActiveUsersSummaryResponse
	enableComparison?: boolean
}

const InsightCard = ({
	title,
	Icon,
	bgClass,
	iconClass,
	titleClass,
	current,
	comparison,
	hasComparison,
	showInternalNote = false,
}: {
	title: string
	Icon: React.ComponentType<{ className?: string }>
	bgClass: string
	iconClass: string
	titleClass: string
	current: PeakLowData
	comparison?: PeakLowData
	hasComparison: boolean
	showInternalNote?: boolean
}) => (
	<div className={bgClass}>
		<div className="flex items-center justify-between gap-1.5">
			<div className="flex items-center gap-1.5">
				<Icon className={iconClass} />
				<h4 className={titleClass}>{title}</h4>
			</div>
			{!hasComparison && showInternalNote && (
				<span className="text-xs text-muted-foreground">(ในช่วงเดียวกัน)</span>
			)}
		</div>

		<div className="grid grid-cols-2 gap-2">
			{/* Peak */}
			<div className="rounded-md bg-background/50 p-2">
				<div className="flex items-center gap-1 text-emerald-600">
					<TrendingUp className="h-3 w-3" />
					<span className="text-xs font-medium">พีค</span>
				</div>
				{hasComparison && comparison ? (
					<>
						<div className="mt-1">
							<p className="text-xs text-muted-foreground">ปัจจุบัน:</p>
							<p className="text-sm font-bold">
								{current.peak.label} ({formatUserCount(current.peak.value)})
							</p>
						</div>
						<div className="mt-1 border-t pt-1">
							<p className="text-xs text-muted-foreground">เปรียบเทียบ:</p>
							<p className="text-sm font-bold text-orange-600 dark:text-orange-400">
								{comparison.peak.label} (
								{formatUserCount(comparison.peak.value)})
							</p>
						</div>
					</>
				) : (
					<>
						<p className="mt-1 text-sm font-bold">{current.peak.label}</p>
						<p className="text-xs text-muted-foreground">
							{formatUserCount(current.peak.value)}
						</p>
					</>
				)}
			</div>

			{/* Low */}
			<div className="rounded-md bg-background/50 p-2">
				<div className="flex items-center gap-1 text-orange-600">
					<TrendingDown className="h-3 w-3" />
					<span className="text-xs font-medium">ต่ำ</span>
				</div>
				{hasComparison && comparison ? (
					<>
						<div className="mt-1">
							<p className="text-xs text-muted-foreground">ปัจจุบัน:</p>
							<p className="text-sm font-bold">
								{current.low.label} ({formatUserCount(current.low.value)})
							</p>
						</div>
						<div className="mt-1 border-t pt-1">
							<p className="text-xs text-muted-foreground">เปรียบเทียบ:</p>
							<p className="text-sm font-bold text-orange-600 dark:text-orange-400">
								{comparison.low.label} ({formatUserCount(comparison.low.value)})
							</p>
						</div>
					</>
				) : (
					<>
						<p className="mt-1 text-sm font-bold">{current.low.label}</p>
						<p className="text-xs text-muted-foreground">
							{formatUserCount(current.low.value)}
						</p>
					</>
				)}
			</div>
		</div>
	</div>
)

export default function ComparisonInsights({
	data,
	comparisonData,
	enableComparison = false,
}: ComparisonInsightsProps) {
	const insights = useMemo(() => {
		if (!data?.hourlyPattern || !data?.dayOfWeek) return null

		const hasComparison = enableComparison && !!comparisonData

		// Current period data
		const peakHour = findPeak(data.hourlyPattern)
		const lowHour = findLowest(data.hourlyPattern)
		const peakDay = findPeak(data.dayOfWeek)
		const lowDay = findLowest(data.dayOfWeek)

		// Safety check: if any required data is missing, return null
		if (!peakHour || !lowHour || !peakDay || !lowDay) return null

		const currentHourly: PeakLowData = {
			peak: {
				label: formatHour(peakHour.hour),
				value: peakHour.average,
				raw: peakHour,
			},
			low: {
				label: formatHour(lowHour.hour),
				value: lowHour.average,
				raw: lowHour,
			},
		}

		const currentDaily: PeakLowData = {
			peak: { label: peakDay.day, value: peakDay.average, raw: peakDay },
			low: { label: lowDay.day, value: lowDay.average, raw: lowDay },
		}

		// Comparison period data
		let comparisonHourly: PeakLowData | undefined
		let comparisonDaily: PeakLowData | undefined

		if (
			hasComparison &&
			comparisonData.hourlyPattern &&
			comparisonData.dayOfWeek
		) {
			const compPeakHour = findPeak(comparisonData.hourlyPattern)
			const compLowHour = findLowest(comparisonData.hourlyPattern)
			const compPeakDay = findPeak(comparisonData.dayOfWeek)
			const compLowDay = findLowest(comparisonData.dayOfWeek)

			// Only set comparison data if all values are present
			if (compPeakHour && compLowHour && compPeakDay && compLowDay) {
				comparisonHourly = {
					peak: {
						label: formatHour(compPeakHour.hour),
						value: compPeakHour.average,
						raw: compPeakHour,
					},
					low: {
						label: formatHour(compLowHour.hour),
						value: compLowHour.average,
						raw: compLowHour,
					},
				}

				comparisonDaily = {
					peak: {
						label: compPeakDay.day,
						value: compPeakDay.average,
						raw: compPeakDay,
					},
					low: {
						label: compLowDay.day,
						value: compLowDay.average,
						raw: compLowDay,
					},
				}
			}
		}

		return {
			currentHourly,
			currentDaily,
			comparisonHourly,
			comparisonDaily,
			hasComparison,
		}
	}, [data, comparisonData, enableComparison])

	if (!insights) return null

	return (
		<div className="overflow-hidden rounded-xl border bg-gradient-to-br from-amber-500/5 to-orange-500/5 p-3 shadow-sm sm:p-4">
			<div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
				<div className="flex items-center gap-2">
					<div className="rounded-lg bg-amber-500/10 p-1.5">
						<Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
					</div>
					<h3 className="text-sm font-bold tracking-tight sm:text-base">
						สรุปข้อมูลเชิงลึก
					</h3>
				</div>
				{insights.hasComparison && (
					<Badge
						variant="outline"
						className="border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300"
					>
						<TrendingUp className="mr-1 h-3 w-3" />
						เปรียบเทียบแล้ว
					</Badge>
				)}
			</div>

			<div className="grid gap-3 md:grid-cols-2">
				<InsightCard
					title="ช่วงเวลาจำนวนผู้ใช้งานตามชั่วโมง"
					Icon={Clock}
					bgClass="space-y-2 rounded-lg border border-blue-500/20 bg-blue-500/5 p-3"
					iconClass="h-4 w-4 text-blue-600 dark:text-blue-400"
					titleClass="text-xs font-semibold text-blue-900 dark:text-blue-100"
					current={insights.currentHourly}
					comparison={insights.comparisonHourly}
					hasComparison={insights.hasComparison}
					showInternalNote
				/>
				<InsightCard
					title="จำนวนผู้ใช้งานตามวัน"
					Icon={Calendar}
					bgClass="space-y-2 rounded-lg border border-purple-500/20 bg-purple-500/5 p-3"
					iconClass="h-4 w-4 text-purple-600 dark:text-purple-400"
					titleClass="text-xs font-semibold text-purple-900 dark:text-purple-100"
					current={insights.currentDaily}
					comparison={insights.comparisonDaily}
					hasComparison={insights.hasComparison}
					showInternalNote
				/>
			</div>
		</div>
	)
}
